<template>
    <span class="employee-phone-menu" @click.stop @mousedown.stop @pointerdown.stop
          @touchstart.stop @dragstart.stop.prevent @keydown.stop>
        <button v-if="actions.display" :id="targetId" ref="trigger" type="button"
                class="employee-phone-menu__trigger" draggable="false"
                aria-haspopup="menu" :aria-expanded="open ? 'true' : 'false'"
                :aria-controls="open ? `${targetId}-menu` : null"
                :aria-label="`Действия с номером ${actions.display}`"
                title="Нажмите на номер: звонок, Telegram, копирование"
                @click="toggle" @keydown.down.prevent="showMenu">
            <slot>{{ actions.display }}</slot>
            <i class="fas fa-chevron-down employee-phone-menu__chevron" aria-hidden="true"></i>
        </button>
        <b-popover v-if="actions.display" :target="targetId" :show.sync="open"
                   triggers="manual" placement="bottom" container="body" boundary="viewport"
                   custom-class="employee-phone-popover" @shown="focusFirst">
            <div :id="`${targetId}-menu`" ref="menu" role="menu"
                 :aria-label="telegramOpen ? 'Поиск в Telegram' : `Действия с номером ${actions.display}`"
                 @click.stop @pointerdown.stop @mousedown.stop @keydown="navigate">
                <template v-if="!telegramOpen">
                    <a v-if="actions.call" role="menuitem" :href="actions.call" @click="close()">
                        <i class="fas fa-phone" aria-hidden="true"></i>Позвонить
                    </a>
                    <button v-if="username" ref="telegramTrigger" type="button" role="menuitem"
                            aria-haspopup="menu" @click="showTelegramMenu" @keydown.right.prevent="showTelegramMenu">
                        <i class="fab fa-telegram-plane" aria-hidden="true"></i>Попробовать найти в Telegram
                        <i class="fas fa-chevron-right employee-phone-menu__submenu-chevron" aria-hidden="true"></i>
                    </button>
                    <a v-else-if="actions.telegram" role="menuitem" :href="actions.telegram"
                       target="_blank" rel="noopener noreferrer" @click="close()">
                        <i class="fab fa-telegram-plane" aria-hidden="true"></i>Попробовать найти в Telegram
                    </a>
                    <button type="button" role="menuitem" @click="copyNumber">
                        <i class="far fa-copy" aria-hidden="true"></i>Скопировать номер
                    </button>
                </template>
                <template v-else>
                    <button type="button" role="menuitem" @click="showMainMenu">
                        <i class="fas fa-chevron-left" aria-hidden="true"></i>Назад
                    </button>
                    <a ref="telegramUsernameLink" role="menuitem" :href="usernameHref"
                       target="_blank" rel="noopener noreferrer" @click="close()">
                        <i class="fas fa-at" aria-hidden="true"></i>
                        <span class="employee-phone-menu__username">По нику @{{ username }}</span>
                    </a>
                    <a v-if="actions.telegram" role="menuitem" :href="actions.telegram"
                       :aria-describedby="`${targetId}-telegram-hint`"
                       target="_blank" rel="noopener noreferrer" @click="close()">
                        <i class="fas fa-phone" aria-hidden="true"></i>По номеру
                    </a>
                    <p v-if="actions.telegram" :id="`${targetId}-telegram-hint`"
                       class="employee-phone-menu__hint">
                        Если по нику не удалось найти человека, попробуйте по номеру телефона.
                    </p>
                </template>
            </div>
        </b-popover>
    </span>
</template>

<script>
import {phoneMenuActions} from '@/utils/phone';
import {telegramUsername, telegramHref} from '@/utils/employeeTelegram';

let sequence = 0;
export default {
    name: 'EmployeePhoneMenu',
    props: {
        value: {type: [String, Number], default: ''},
        telegramUsername: {type: String, default: ''}
    },
    data() {
        return {targetId: `employee-phone-${++sequence}`, open: false, telegramOpen: false};
    },
    computed: {
        actions() { return phoneMenuActions(this.value); },
        username() { return telegramUsername(this.telegramUsername); },
        usernameHref() { return this.username ? telegramHref(this.username) : null; }
    },
    watch: {
        value() { this.close(false); },
        telegramUsername() { this.close(false); },
        open(value) {
            if (!value) this.telegramOpen = false;
            this.$emit('menu-open', value);
            if (value) this.$root.$emit('employee-phone-menu-open', this.targetId);
        }
    },
    mounted() {
        document.addEventListener('click', this.outsideClick, true);
        document.addEventListener('keydown', this.escape, true);
        this.$root.$on('employee-phone-menu-open', this.otherMenu);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.outsideClick, true);
        document.removeEventListener('keydown', this.escape, true);
        this.$root.$off('employee-phone-menu-open', this.otherMenu);
        if (this.open) this.$emit('menu-open', false);
    },
    methods: {
        toggle() { this.open = !this.open; },
        showMenu() { this.open = true; },
        showTelegramMenu() {
            this.telegramOpen = true;
            this.$nextTick(() => window.requestAnimationFrame(() => {
                if (this.open && this.telegramOpen && this.$refs.telegramUsernameLink) this.$refs.telegramUsernameLink.focus();
            }));
        },
        showMainMenu() {
            this.telegramOpen = false;
            this.$nextTick(() => window.requestAnimationFrame(() => {
                if (this.open && !this.telegramOpen && this.$refs.telegramTrigger) this.$refs.telegramTrigger.focus();
            }));
        },
        close(restoreFocus = true) {
            this.open = false;
            if (restoreFocus && this.$refs.trigger) this.$refs.trigger.focus();
        },
        otherMenu(id) { if (id !== this.targetId) this.close(false); },
        outsideClick(event) {
            if (this.open && !this.$el.contains(event.target)
                && !(this.$refs.menu && this.$refs.menu.contains(event.target))) this.close(false);
        },
        escape(event) {
            if (this.open && event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                if (this.telegramOpen) this.showMainMenu();
                else this.close();
            }
        },
        focusFirst() {
            const first = this.$refs.menu && this.$refs.menu.querySelector('[role="menuitem"]');
            if (first) first.focus();
        },
        navigate(event) {
            event.stopPropagation();
            if (event.key === 'ArrowLeft' && this.telegramOpen) {
                event.preventDefault();
                this.showMainMenu();
                return;
            }
            if (event.key === 'Tab') {
                this.close();
                return;
            }
            if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
            event.preventDefault();
            const items = Array.from(this.$refs.menu.querySelectorAll('[role="menuitem"]'));
            const current = items.indexOf(document.activeElement);
            const next = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1
                : (current + (event.key === 'ArrowUp' ? -1 : 1) + items.length) % items.length;
            items[next].focus();
        },
        fallbackCopy(value) {
            const field = document.createElement('textarea');
            field.value = value;
            field.style.cssText = 'position:fixed;left:-9999px;top:0';
            field.setAttribute('readonly', '');
            document.body.appendChild(field);
            try {
                field.select();
                return document.execCommand('copy');
            } finally {
                document.body.removeChild(field);
            }
        },
        async copyNumber() {
            const value = this.actions.copy;
            let copied = false;
            try {
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(value);
                    copied = true;
                }
            } catch (error) { /* Fall back when clipboard permission is unavailable. */ }
            if (!copied) {
                try { copied = this.fallbackCopy(value); } catch (error) { /* Show the error below. */ }
            }
            this.close();
            this.$bvToast.toast(copied ? 'Номер скопирован' : 'Не удалось скопировать номер. Выделите и скопируйте его вручную.', {
                title: 'Телефон', variant: copied ? 'success' : 'warning', autoHideDelay: 3500
            });
        }
    }
};
</script>

<style>
.employee-phone-menu { display: inline-block; max-width: 100%; }
.employee-phone-menu__trigger {
    display: inline-flex; align-items: baseline; gap: 5px;
    padding: 0; border: 0; background: transparent; color: inherit;
    font: inherit; font-variant-numeric: tabular-nums; line-height: inherit;
    text-align: left; white-space: nowrap; cursor: pointer;
    text-decoration: underline dotted; text-underline-offset: 3px;
}
.employee-phone-menu__chevron { font-size: 0.65em; opacity: 0.65; }
.employee-phone-menu__trigger:hover { text-decoration: underline; }
.employee-phone-menu__trigger:focus-visible { outline: 2px solid #1683ff; outline-offset: 3px; }
/* Appended to body: stay above the employee inspector layer (1090). */
.popover.employee-phone-popover { max-width: calc(100vw - 24px); width: 290px; z-index: 1100; }
.employee-phone-popover .popover-body { padding: 4px; }
.employee-phone-popover [role="menuitem"] {
    display: flex; align-items: center; gap: 10px; width: 100%; min-height: 44px;
    padding: 9px 12px; border: 0; border-radius: 4px; background: transparent;
    color: #26394c; font-size: 14px; line-height: 1.4; text-align: left;
    white-space: normal; cursor: pointer;
}
.employee-phone-popover [role="menuitem"]:hover,
.employee-phone-popover [role="menuitem"]:focus { background: #edf5ff; color: #1266bf; text-decoration: none; }
.employee-phone-popover [role="menuitem"] i { flex: 0 0 16px; text-align: center; }
.employee-phone-menu__username { min-width: 0; overflow-wrap: anywhere; }
.employee-phone-menu__submenu-chevron { margin-left: auto; font-size: 10px; }
.employee-phone-menu__hint {
    margin: 0 12px 6px 38px; color: #5d6d7e; font-size: 12px; line-height: 1.4;
}
</style>
